import { supabase } from '@/lib/supabase';

// Base entity class for Supabase operations
class SupabaseEntity {
  constructor(tableName) {
    this.tableName = tableName;
  }

  async list(orderBy = 'created_at', ascending = false, limit = null) {
    let query = supabase
      .from(this.tableName)
      .select('*');
    
    if (orderBy) {
      query = query.order(orderBy, { ascending });
    }
    
    if (limit) {
      query = query.limit(limit);
    }
    
    const { data, error } = await query;
    if (error) throw error;
    return data || [];
  }

  async get(id) {
    const { data, error } = await supabase
      .from(this.tableName)
      .select('*')
      .eq('id', id)
      .single();
    
    if (error) throw error;
    return data;
  }

  async create(itemData) {
    const { data, error } = await supabase
      .from(this.tableName)
      .insert(itemData)
      .select()
      .single();
    
    if (error) throw error;
    return data;
  }

  async update(id, updates) {
    const { data, error } = await supabase
      .from(this.tableName)
      .update(updates)
      .eq('id', id)
      .select()
      .single();
    
    if (error) throw error;
    return data;
  }

  async delete(id) {
    const { error } = await supabase
      .from(this.tableName)
      .delete()
      .eq('id', id);
    
    if (error) throw error;
    return { success: true };
  }

  async filter(filters, orderBy = 'created_at', ascending = false, limit = null) {
    let query = supabase.from(this.tableName).select('*');
    
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        query = query.eq(key, value);
      }
    });
    
    if (orderBy) {
      query = query.order(orderBy, { ascending });
    }
    
    if (limit) {
      query = query.limit(limit);
    }
    
    const { data, error } = await query;
    if (error) throw error;
    return data || [];
  }
}

// Specialized entity classes
class UniversityEntity extends SupabaseEntity {
  constructor() {
    super('universities');
  }
}

class UserProfileEntity extends SupabaseEntity {
  constructor() {
    super('user_profiles');
  }

  async getCurrentProfile() {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase
      .from('user_profiles')
      .select('*, university:universities(*)')
      .eq('user_id', user.id)
      .single();

    if (error) throw error;
    return data;
  }
}

class AgentEntity extends SupabaseEntity {
  constructor() {
    super('agents');
  }

  async update(id, updates) {
    const { data, error } = await supabase
      .from(this.tableName)
      .update(updates)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;

    if (updates.status && data.user_profile_id) {
      const { error: profileError } = await supabase
        .from('user_profiles')
        .update({ status: updates.status })
        .eq('id', data.user_profile_id);

      if (profileError) {
        console.error('Failed to update user profile status:', profileError);
      }
    }

    return data;
  }
}

class ApplicationEntity extends SupabaseEntity {
  constructor() {
    super('applications');
  }
}

class CommissionEntity extends SupabaseEntity {
  constructor() {
    super('commissions');
  }
}

class DocumentEntity extends SupabaseEntity {
  constructor() {
    super('documents');
  }
}

class InvitationEntity extends SupabaseEntity {
  constructor() {
    super('invitations');
  }

  async acceptInvitation(token, userId) {
    const { data: invitation, error: invError } = await supabase
      .from('invitations')
      .select('*')
      .eq('invitation_token', token)
      .eq('status', 'pending')
      .maybeSingle();

    if (invError) throw invError;
    if (!invitation) throw new Error('Invitation not found or already accepted');

    if (new Date(invitation.expires_at) < new Date()) {
      throw new Error('Invitation has expired');
    }

    const { data: existingProfile, error: profileCheckError } = await supabase
      .from('user_profiles')
      .select('*')
      .eq('user_id', userId)
      .maybeSingle();

    if (profileCheckError) throw profileCheckError;

    let profile;
    if (existingProfile) {
      const { data: updatedProfile, error: updateError } = await supabase
        .from('user_profiles')
        .update({
          role: invitation.role,
          university_id: invitation.university_id,
          status: 'active',
          updated_at: new Date().toISOString()
        })
        .eq('user_id', userId)
        .select()
        .single();

      if (updateError) throw updateError;
      profile = updatedProfile;
    } else {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not found');

      const { data: newProfile, error: createError } = await supabase
        .from('user_profiles')
        .insert({
          user_id: userId,
          email: invitation.email,
          full_name: user.email.split('@')[0],
          role: invitation.role,
          university_id: invitation.university_id,
          status: 'active'
        })
        .select()
        .single();

      if (createError) throw createError;
      profile = newProfile;
    }

    const { error: roleError } = await supabase
      .from('user_university_roles')
      .upsert({
        user_id: userId,
        university_id: invitation.university_id,
        role: invitation.role,
        is_active: true
      }, {
        onConflict: 'user_id,university_id'
      });

    if (roleError) throw roleError;

    const { error: invUpdateError } = await supabase
      .from('invitations')
      .update({
        status: 'accepted',
        accepted_at: new Date().toISOString()
      })
      .eq('id', invitation.id);

    if (invUpdateError) throw invUpdateError;

    return profile;
  }
}

class AuditLogEntity extends SupabaseEntity {
  constructor() {
    super('audit_logs');
  }
}

// Create entity instances
export const University = new UniversityEntity();
export const UserProfile = new UserProfileEntity();
export const Agent = new AgentEntity();
export const Application = new ApplicationEntity();
export const Commission = new CommissionEntity();
export const Document = new DocumentEntity();
export const Invitation = new InvitationEntity();
export const AuditLog = new AuditLogEntity();

// Export auth service as User for backward compatibility
export const User = {
  async me() {
    return await UserProfile.getCurrentProfile();
  },
  
  async login() {
    // This will be handled by the auth context
    throw new Error('Use AuthContext for login');
  },
  
  async logout() {
    return await AuthService.signOut();
  },
  
  async updateMyUserData(updates) {
    const profile = await UserProfile.getCurrentProfile();
    return await UserProfile.update(profile.id, updates);
  },
  
  isAuthenticated() {
    return !!AuthService.getCurrentUser();
  }
};